import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { trackPromise } from "react-promise-tracker";
import { WALEE_BASE_URL } from "&config/url";
import { TwoFactor } from "./twoFactor.type";
import { RootState } from "&store/store";

const initialState: TwoFactor = {
  isTwoFactorPending: false,
};

const getToken = (state: RootState) => state.login.token as string;

const verifyLoginOtp = createAsyncThunk(
  "twoFactor/verifyLoginOtp",
  async ({ otp, token }: { otp: string; token: string }, { rejectWithValue }) => {
    try {
      const response = await trackPromise(
        axios.post(
          WALEE_BASE_URL.concat("/auth/2fa/verify-login"),
          { otp },
          { headers: { "x-auth-token": token } }
        )
      );
      return response.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data);
    }
  }
);

const getAuthenticatorStatus = createAsyncThunk(
  "twoFactor/getStatus",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getToken(getState() as RootState);
      const response = await trackPromise(
        axios.get(WALEE_BASE_URL.concat("/auth/2fa/status"), {
          headers: { "x-auth-token": token },
        })
      );
      return response.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data);
    }
  }
);

const generateAuthenticator = createAsyncThunk(
  "twoFactor/generate",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getToken(getState() as RootState);
      const response = await trackPromise(
        axios.post(
          WALEE_BASE_URL.concat("/auth/2fa/generate"),
          {},
          { headers: { "x-auth-token": token } }
        )
      );
      return response.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data);
    }
  }
);

const verifySetupOtp = createAsyncThunk(
  "twoFactor/verifySetup",
  async ({ otp }: { otp: string }, { getState, rejectWithValue }) => {
    try {
      const token = getToken(getState() as RootState);
      const response = await trackPromise(
        axios.post(
          WALEE_BASE_URL.concat("/auth/2fa/verify-setup"),
          { otp },
          { headers: { "x-auth-token": token } }
        )
      );
      return response.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data);
    }
  }
);

const removeAuthenticator = createAsyncThunk(
  "twoFactor/remove",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getToken(getState() as RootState);
      const response = await trackPromise(
        axios.delete(WALEE_BASE_URL.concat("/auth/2fa"), {
          headers: { "x-auth-token": token },
        })
      );
      return response.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data);
    }
  }
);

const twoFactorSlice = createSlice({
  name: "twoFactor",
  initialState,
  reducers: {
    setTwoFactorPending: (state, action) => {
      state.isTwoFactorPending = action.payload;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(verifyLoginOtp.rejected, (_, { payload }: any) => {
      const error = payload?.message || "Invalid OTP code";
      toast.error(error, { position: "top-center", autoClose: 5000 });
    });
    builder.addCase(verifySetupOtp.rejected, (_, { payload }: any) => {
      const error = payload?.message || "Invalid OTP code";
      toast.error(error, { position: "top-center", autoClose: 5000 });
    });
    builder.addCase(removeAuthenticator.rejected, (_, { payload }: any) => {
      const error = payload?.message || "Failed to remove authenticator";
      toast.error(error, { position: "top-center", autoClose: 5000 });
    });
  },
});

export const twoFactorReducer = twoFactorSlice.reducer;
export const twoFactorActions = {
  ...twoFactorSlice.actions,
  verifyLoginOtp,
  getAuthenticatorStatus,
  generateAuthenticator,
  verifySetupOtp,
  removeAuthenticator,
};
